import React from 'react';
import AdminLayout from '../../../components/Layout/AdminLayout';
import { Button, Table, Modal, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { Globe, Rocket, Loader2, CheckCircle, XCircle, AlertTriangle, RefreshCw, StopCircle, AlertCircle, Check, X, Edit2 } from 'lucide-react';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import s from './style.css';
import cs from '../../../components/commonStyle.css';
import fetch from '../../../core/fetch';

// Import static language files for editing
import enUS from '../../../messages/en-US.json';
import frFR from '../../../messages/fr-FR.json';
import ptPT from '../../../messages/pt-PT.json';
import es from '../../../messages/es.json';
import itIT from '../../../messages/it-IT.json';

const staticLanguageMessages = {
  'en-US': enUS,
  'fr-FR': frFR,
  'pt-PT': ptPT,
  'es': es,
  'it-IT': itIT,
};

class LanguageManagement extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dbLanguages: [], // Languages from database
      showLanguageModal: false,
      editingLang: null,
      editingJson: '',
      uploadError: null,
      successMessage: null,
      emailTemplates: {}, // Templates grouped by templateType -> locale
      showTemplateModal: false,
      selectedTemplate: null,
      editingTemplateType: null,
      editingLocale: null,
      loading: true,
      togglingId: null, // Track which language is being toggled
      // Search-related state
      searchTerm: '',
      searchMatches: [],
      currentMatchIndex: 0,
      // Batch translation state
      translating: false,
      translationResult: null,
      forceOverwrite: false,  // NEW: Force re-translate everything
      // Progress tracking
      batchProgress: null,  // { current: 1, total: 7, section: 'ListSettings', results: [] }
      // Cancellation flag
      translationCancelled: false
    };
    // Refs
    this.textareaRef = React.createRef();
    this.searchInputRef = React.createRef();
    // CRITICAL: Use ref for immediate cancellation (state updates are async)
    this.isCancelledRef = { current: false };
  }

  componentDidMount() {
    this.loadDbLanguages();
    this.loadEmailTemplates();
  }

  // Load languages from database via GraphQL
  async loadDbLanguages() {
    try {
      const query = `
        query {
          getLanguages {
            id
            title
            code
            fileURL
            isActive
          }
        }
      `;

      const response = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query }),
      });

      const { data, errors } = await response.json();

      if (errors) {
        this.setState({ uploadError: errors[0].message, loading: false });
        return;
      }

      this.setState({
        dbLanguages: data.getLanguages || [],
        loading: false,
      });
    } catch (error) {
      this.setState({
        uploadError: `Error loading languages: ${error.message}`,
        loading: false,
      });
    }
  }

  // Toggle language active status
  handleToggleStatus = async (language) => {
    const { dbLanguages } = this.state;
    const newStatus = !language.isActive;

    // Check if this is the first (default) language and trying to deactivate
    const sortedLanguages = [...dbLanguages].sort((a, b) => a.id - b.id);
    const isDefaultLanguage = sortedLanguages.length > 0 && sortedLanguages[0].id === language.id;

    if (isDefaultLanguage && !newStatus) {
      this.setState({
        uploadError: 'Cannot deactivate the default language (first language). It must always be active.',
        successMessage: null,
      });
      return;
    }

    // Optimistic update
    const previousLanguages = [...dbLanguages];
    this.setState({
      dbLanguages: dbLanguages.map(lang =>
        lang.id === language.id ? { ...lang, isActive: newStatus } : lang
      ),
      togglingId: language.id,
      uploadError: null,
      successMessage: null,
    });

    try {
      const mutation = `
        mutation ToggleLanguageStatus($id: Int!, $isActive: Boolean!) {
          toggleLanguageStatus(id: $id, isActive: $isActive) {
            status
            errorMessage
          }
        }
      `;

      const response = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          query: mutation,
          variables: { id: language.id, isActive: newStatus },
        }),
      });

      const { data, errors } = await response.json();

      if (errors || (data.toggleLanguageStatus && data.toggleLanguageStatus.status !== 200)) {
        // Revert on error
        const errorMsg = errors ? errors[0].message : data.toggleLanguageStatus.errorMessage;
        this.setState({
          dbLanguages: previousLanguages,
          uploadError: errorMsg || 'Failed to update language status',
          togglingId: null,
        });
        return;
      }

      this.setState({
        togglingId: null,
        successMessage: `${language.title} has been ${newStatus ? 'activated' : 'deactivated'}`,
      });

      // Clear success message after 3 seconds
      setTimeout(() => {
        this.setState({ successMessage: null });
      }, 3000);
    } catch (error) {
      // Revert on error
      this.setState({
        dbLanguages: previousLanguages,
        uploadError: `Error: ${error.message}`,
        togglingId: null,
      });
    }
  };

  async loadEmailTemplates() {
    try {
      const query = `
        query {
          getEmailTemplates {
            id
            templateType
            locale
            subject
            heading
            content
            buttonText
          }
        }
      `;

      const response = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query }),
      });

      const { data, errors } = await response.json();
      
      if (errors) {
        console.error('GraphQL errors:', errors);
        this.setState({ emailTemplates: [] });
        return;
      }

      // Group templates by templateType
      const templates = data.getEmailTemplates || [];
      const groupedTemplates = {};
      templates.forEach(t => {
        if (!groupedTemplates[t.templateType]) {
          groupedTemplates[t.templateType] = {};
        }
        groupedTemplates[t.templateType][t.locale] = t;
      });

      this.setState({ emailTemplates: groupedTemplates });
    } catch (e) {
      console.error('Failed to load email templates:', e);
      this.setState({ emailTemplates: {} });
    }
  }

  handleEditEmailTemplate = (templateType, locale) => {
    const { emailTemplates, dbLanguages } = this.state;
    const template = emailTemplates[templateType]?.[locale] || {
      templateType,
      locale,
      subject: '',
      heading: '',
      content: '',
      buttonText: ''
    };

    this.setState({
      showTemplateModal: true,
      selectedTemplate: { ...template },
      editingTemplateType: templateType,
      editingLocale: locale
    });
  };

  handleSaveEmailTemplate = async () => {
    const { selectedTemplate } = this.state;
    
    try {
      const mutation = `
        mutation UpdateEmailTemplate(
          $id: Int,
          $templateType: String!,
          $locale: String!,
          $subject: String!,
          $heading: String,
          $content: String!,
          $buttonText: String
        ) {
          updateEmailTemplate(
            id: $id,
            templateType: $templateType,
            locale: $locale,
            subject: $subject,
            heading: $heading,
            content: $content,
            buttonText: $buttonText
          ) {
            status
            errorMessage
          }
        }
      `;

      const response = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          query: mutation,
          variables: {
            id: selectedTemplate.id || null,
            templateType: selectedTemplate.templateType,
            locale: selectedTemplate.locale,
            subject: selectedTemplate.subject,
            heading: selectedTemplate.heading || '',
            content: selectedTemplate.content,
            buttonText: selectedTemplate.buttonText || ''
          }
        }),
      });

      const { data, errors } = await response.json();

      if (errors || data.updateEmailTemplate.status !== 200) {
        const errorMsg = errors ? errors[0].message : data.updateEmailTemplate.errorMessage;
        this.setState({ uploadError: errorMsg || 'Failed to save template' });
        return;
      }

      this.setState({
        showTemplateModal: false,
        selectedTemplate: null,
        successMessage: 'Template saved successfully!'
      });

      // Reload templates
      this.loadEmailTemplates();

      setTimeout(() => this.setState({ successMessage: null }), 3000);
    } catch (e) {
      this.setState({ uploadError: `Error saving template: ${e.message}` });
    }
  };

  handleEdit = (lang) => {
    // Get messages from static files if available
    const messages = staticLanguageMessages[lang.code] || {};
    this.setState({
      editingLang: lang,
      editingJson: JSON.stringify(messages, null, 2),
      showLanguageModal: true,
      uploadError: null,
      successMessage: null,
      searchTerm: '',
      searchMatches: [],
      currentMatchIndex: 0,
    });
  };

  handleSearchChange = (e) => {
    const searchTerm = e.target.value;
    this.setState({ searchTerm }, () => {
      this.updateSearchMatches();
    });
  };

  updateSearchMatches = () => {
    const { editingJson, searchTerm } = this.state;
    if (!searchTerm) {
      this.setState({ searchMatches: [], currentMatchIndex: 0 });
      return;
    }
    const matches = [];
    let idx = 0;
    const lowerJson = editingJson.toLowerCase();
    const lowerSearch = searchTerm.toLowerCase();
    while ((idx = lowerJson.indexOf(lowerSearch, idx)) !== -1) {
      matches.push(idx);
      idx += lowerSearch.length;
    }
    this.setState({
      searchMatches: matches,
      currentMatchIndex: matches.length > 0 ? 0 : -1
    });
  };

  scrollToMatch = (matchIdx) => {
    const { searchMatches, searchTerm, editingJson } = this.state;
    if (!this.textareaRef.current || !searchMatches.length || matchIdx < 0) return;

    const textarea = this.textareaRef.current;
    const pos = searchMatches[matchIdx];

    // Calculate line number and scroll position
    const before = editingJson.slice(0, pos);
    const lineNumber = before.split('\n').length - 1;

    // Get computed line height
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 20;

    // Calculate scroll position to center the match
    const textareaHeight = textarea.clientHeight;
    const visibleLines = Math.floor(textareaHeight / lineHeight);
    const targetLine = Math.max(0, lineNumber - Math.floor(visibleLines / 2));
    const targetScrollTop = targetLine * lineHeight;

    // Scroll without stealing focus
    textarea.scrollTop = targetScrollTop;

    // Select the text
    textarea.setSelectionRange(pos, pos + searchTerm.length);

    // Force a small delay to ensure the scroll happens
    setTimeout(() => {
      textarea.scrollTop = targetScrollTop;
    }, 10);
  };

  handleSearchUp = () => {
    const { currentMatchIndex, searchMatches } = this.state;
    if (!searchMatches.length) return;
    const newIndex = (currentMatchIndex - 1 + searchMatches.length) % searchMatches.length;
    this.setState({ currentMatchIndex: newIndex }, () => {
      this.scrollToMatchWithFocus(newIndex);
    });
  };

  handleSearchDown = () => {
    const { currentMatchIndex, searchMatches } = this.state;
    if (!searchMatches.length) return;
    const newIndex = (currentMatchIndex + 1) % searchMatches.length;
    this.setState({ currentMatchIndex: newIndex }, () => {
      this.scrollToMatchWithFocus(newIndex);
    });
  };

  scrollToMatchWithFocus = (matchIdx) => {
    const { searchMatches, searchTerm, editingJson } = this.state;
    if (!this.textareaRef.current || !searchMatches.length || matchIdx < 0) return;

    const textarea = this.textareaRef.current;
    const pos = searchMatches[matchIdx];

    // Focus and select the text (only when using navigation buttons)
    textarea.focus();
    textarea.setSelectionRange(pos, pos + searchTerm.length);

    // Calculate line number and scroll position
    const before = editingJson.slice(0, pos);
    const lineNumber = before.split('\n').length - 1;

    // Get computed line height
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 20;

    // Calculate scroll position to center the match
    const textareaHeight = textarea.clientHeight;
    const visibleLines = Math.floor(textareaHeight / lineHeight);
    const targetLine = Math.max(0, lineNumber - Math.floor(visibleLines / 2));
    const targetScrollTop = targetLine * lineHeight;

    // Scroll to the position
    textarea.scrollTop = targetScrollTop;

    // Force a small delay to ensure the scroll happens
    setTimeout(() => {
      textarea.scrollTop = targetScrollTop;
    }, 10);
  };

  handleJsonChange = (e) => {
    this.setState({ editingJson: e.target.value }, () => {
      // Re-calculate matches if there's an active search
      if (this.state.searchTerm) {
        this.updateSearchMatches();
      }
    });
  };

  handleSave = async () => {
    const { editingJson, editingLang } = this.state;
    try {
      const parsed = JSON.parse(editingJson);

      const res = await fetch('/admin/update-language-file', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: editingLang.code, messages: parsed }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          this.setState({
            editingJson: JSON.stringify(data.messages, null, 2),
            uploadError: null,
            successMessage: 'Language file saved successfully!',
            showLanguageModal: false
          });
          // Clear success message after 3 seconds
          setTimeout(() => {
            this.setState({ successMessage: null });
          }, 3000);
        } else {
          this.setState({ uploadError: data.error || 'Failed to save file' });
        }
      } else {
        const errorData = await res.json();
        this.setState({ uploadError: errorData.error || 'Failed to save file' });
      }
    } catch (e) {
      this.setState({ uploadError: 'Invalid JSON: ' + e.message });
    }
  };

  handleEditTemplate = (template) => {
    this.setState({ selectedTemplate: template, showTemplateModal: true });
  };

  handleSaveTemplate = async () => {
    const { selectedTemplate } = this.state;
    const response = await fetch(`/web-api/admin/email-templates/${selectedTemplate.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        subject: selectedTemplate.subject,
        content: selectedTemplate.content
      })
    });
    if (response.ok) {
      this.setState({ showTemplateModal: false, selectedTemplate: null });
      this.loadEmailTemplates();
    } else {
      const errorData = await response.json();
      this.setState({ uploadError: `Failed to save template: ${errorData.error}` });
    }
  };

  // Translate a single batch from a section (returns hasMore flag)
  translateSectionBatch = async (section, force = false) => {
    const query = `
      mutation TranslateSection($section: String!, $force: Boolean) {
        TranslateAllContent(section: $section, force: $force) {
          status
          translated
          skipped
          errors
          message
          section
          hasMore
          processed
          remaining
        }
      }
    `;
    
    const response = await fetch('/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ 
        query,
        variables: { section, force }
      })
    });
    
    const result = await response.json();
    return result.data?.TranslateAllContent || { status: 'error', message: 'No response', hasMore: false };
  };

  // Process a section completely (loop until hasMore=false)
  processSectionFully = async (section, onProgress, force = false) => {
    let totalTranslated = 0;
    let totalSkipped = 0;
    let totalErrors = 0;
    let batchNum = 0;
    let hasMore = true;
    
    while (hasMore) {
      // Check for cancellation before each batch using REF (immediate)
      if (this.isCancelledRef.current) {
        console.log(`[BatchTranslate] 🛑 ${section} cancelled at batch #${batchNum}`);
        return {
          section,
          status: 'cancelled',
          translated: totalTranslated,
          skipped: totalSkipped,
          errors: totalErrors,
          batches: batchNum,
          message: 'Cancelled by user'
        };
      }
      
      batchNum++;
      onProgress({ section, batchNum, status: 'processing', force });
      
      console.log(`[BatchTranslate] ${section} - Batch #${batchNum}... ${force ? '(FORCE MODE)' : ''}`);
      
      const result = await this.translateSectionBatch(section, force);
      
      if (result.status === 'error') {
        return {
          section,
          status: 'error',
          translated: totalTranslated,
          skipped: totalSkipped,
          errors: totalErrors + 1,
          message: result.message,
          batches: batchNum
        };
      }
      
      totalTranslated += result.translated || 0;
      totalSkipped += result.skipped || 0;
      totalErrors += result.errors || 0;
      hasMore = result.hasMore === true;
      
      onProgress({ 
        section, 
        batchNum, 
        status: hasMore ? 'processing' : 'done',
        remaining: result.remaining || 0,
        translated: totalTranslated
      });
      
      // Small delay between batches
      if (hasMore) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
    
    return {
      section,
      status: 'success',
      translated: totalTranslated,
      skipped: totalSkipped,
      errors: totalErrors,
      batches: batchNum,
      message: `${totalTranslated} translated in ${batchNum} batches`
    };
  };

  // Stop/Cancel translation - uses ref for IMMEDIATE effect
  handleStopTranslation = () => {
    console.log('[BatchTranslate] 🛑 STOP requested by user');
    // Set ref immediately (no async state update delay)
    this.isCancelledRef.current = true;
    this.setState({ 
      translationCancelled: true,
      uploadError: '🛑 Proceso detenido por el usuario'
    });
  };

  // Batch translation with progress and loops
  handleBatchTranslation = async () => {
    const { forceOverwrite } = this.state;
    
    const sections = [
      'ListSettings',
      'StaticPage', 
      'FooterBlock',
      'Banner',
      'ImageBanner',  // ADDED: Was missing - translates homepage banner title/description
      'WhyHostInfoBlock',
      'FindYourVehicleBlock',
      'StaticInfoBlock'
    ];
    
    console.log(`[BatchTranslate] Starting batch translation... Force: ${forceOverwrite}`);
    
    // RESET cancellation ref immediately
    this.isCancelledRef.current = false;
    
    this.setState({ 
      translating: true, 
      translationResult: null, 
      uploadError: null,
      translationCancelled: false,
      batchProgress: { 
        current: 0, 
        total: sections.length, 
        section: '', 
        batchNum: 0,
        remaining: 0,
        force: forceOverwrite,
        results: [] 
      }
    });
    
    const results = [];
    let totalTranslated = 0;
    let totalSkipped = 0;
    let totalErrors = 0;
    
    for (let i = 0; i < sections.length; i++) {
      // CHECK FOR CANCELLATION using REF (immediate, no async delay)
      if (this.isCancelledRef.current) {
        console.log('[BatchTranslate] 🛑 Translation cancelled by user');
        results.push({ section: 'CANCELLED', status: 'cancelled', message: 'Stopped by user' });
        break;
      }
      
      const section = sections[i];
      
      try {
        console.log(`[BatchTranslate] Starting ${section} (${i + 1}/${sections.length})... ${forceOverwrite ? '(FORCE MODE)' : ''}`);
        
        // Process section fully with loop (pass forceOverwrite)
        const result = await this.processSectionFully(section, (progress) => {
          // Also check during progress updates using REF
          if (this.isCancelledRef.current) return;
          
          this.setState({
            batchProgress: { 
              current: i + 1, 
              total: sections.length, 
              section: progress.section,
              batchNum: progress.batchNum,
              remaining: progress.remaining || 0,
              status: progress.status,
              translated: progress.translated || 0,
              force: forceOverwrite,
              results: [...results]
            }
          });
        }, forceOverwrite);
        
        results.push(result);
        
        if (result.status === 'success') {
          totalTranslated += result.translated || 0;
          totalSkipped += result.skipped || 0;
          totalErrors += result.errors || 0;
        } else {
          totalErrors++;
        }
        
      } catch (error) {
        console.error(`[BatchTranslate] Error in ${section}:`, error);
        results.push({
          section,
          status: 'error',
          message: error.message
        });
        totalErrors++;
      }
      
      // Delay between sections
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    const summary = {
      status: totalErrors === 0 ? 'success' : 'partial',
      translated: totalTranslated,
      skipped: totalSkipped,
      errors: totalErrors,
      message: `Completed: ${totalTranslated} translated, ${totalSkipped} skipped, ${totalErrors} errors`,
      details: results
    };
    
    this.setState({ 
      translating: false,
      translationResult: summary,
      batchProgress: null,
      successMessage: summary.message
    });
    
    setTimeout(() => this.setState({ successMessage: null }), 8000);
  };

  render() {
    const {
      dbLanguages,
      loading,
      showLanguageModal,
      editingLang,
      editingJson,
      uploadError,
      successMessage,
      emailTemplates,
      showTemplateModal,
      selectedTemplate,
      togglingId
    } = this.state;

    // Sort languages by id to determine default (first one)
    const sortedLanguages = [...dbLanguages].sort((a, b) => a.id - b.id);
    const defaultLanguageId = sortedLanguages.length > 0 ? sortedLanguages[0].id : null;

    if (loading) {
      return (
        <AdminLayout>
          <div className={s.root}>
            <div className={s.container}>
              <h1>Language Management</h1>
              <p>Loading languages...</p>
            </div>
          </div>
        </AdminLayout>
      );
    }

    return (
      <AdminLayout>
        <div className={s.root}>
          <div className={s.container}>
            <h1>Language Management</h1>

            {/* 🚀 SUPER TRANSLATE BUTTON - TOP OF PAGE */}
            <div style={{ 
              padding: '24px', 
              marginBottom: '24px',
              backgroundColor: '#e3f2fd', 
              borderRadius: '12px',
              border: '3px solid #1976d2',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '12px', color: '#1565c0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Globe size={24} /> AI Batch Translation
              </h2>
              <p style={{ marginBottom: '16px', color: '#37474f' }}>
                Translate all pending content across the site (ListSettings, Banners, Footer, WhyHost, etc.) 
                to English, French, Italian and Portuguese using AI.
              </p>
              
              {/* Progress Bar */}
              {this.state.batchProgress && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#1565c0'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> {this.state.batchProgress.section}
                      {this.state.batchProgress.batchNum > 0 && (
                        <span style={{ fontWeight: 'normal', marginLeft: '8px', fontSize: '13px' }}>
                          (Batch #{this.state.batchProgress.batchNum}
                          {this.state.batchProgress.remaining > 0 && ` - ${this.state.batchProgress.remaining} pending`})
                        </span>
                      )}
                    </span>
                    <span>Section {this.state.batchProgress.current} / {this.state.batchProgress.total}</span>
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '24px', 
                    backgroundColor: '#bbdefb', 
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      width: `${(this.state.batchProgress.current / this.state.batchProgress.total) * 100}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #42a5f5, #1976d2)',
                      borderRadius: '12px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                  {/* Live stats */}
                  {this.state.batchProgress.translated > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '13px', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <CheckCircle size={14} /> {this.state.batchProgress.translated} translated so far in this section
                    </div>
                  )}
                  {/* Results so far */}
                  {this.state.batchProgress.results.length > 0 && (
                    <div style={{ marginTop: '12px', fontSize: '13px' }}>
                      {this.state.batchProgress.results.map((r, idx) => (
                        <div key={idx} style={{ 
                          padding: '4px 8px',
                          backgroundColor: r.status === 'success' ? '#e8f5e9' : '#ffebee',
                          borderRadius: '4px',
                          marginBottom: '4px',
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{r.status === 'success' ? <CheckCircle size={14} /> : <XCircle size={14} />} {r.section}</span>
                          <span>
                            {r.translated || 0} translated
                            {r.batches > 1 && ` (${r.batches} batches)`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <Button 
                  bsStyle="primary"
                  bsSize="large"
                  onClick={this.handleBatchTranslation}
                  disabled={this.state.translating}
                  style={{ 
                    fontSize: '18px', 
                    padding: '14px 32px',
                    background: this.state.translating 
                      ? 'linear-gradient(135deg, #78909c 0%, #546e7a 100%)'
                      : this.state.forceOverwrite 
                        ? 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)'
                        : 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
                    border: 'none'
                  }}
                >
                  {this.state.translating ? (
                    <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} /> Translating... ({this.state.batchProgress?.current || 0}/{this.state.batchProgress?.total || 8}){this.state.batchProgress?.force && ' (FORCE)'}</>
                  ) : (
                    <>{this.state.forceOverwrite ? <><AlertTriangle size={18} style={{ marginRight: '8px' }} /> FORCE Translate Everything</> : <><Rocket size={18} style={{ marginRight: '8px' }} /> Translate Everything (8 Sections)</>}</>
                  )}
                </Button>
                
                {/* 🛑 STOP BUTTON - Only visible when translating */}
                {this.state.translating && (
                  <Button 
                    bsStyle="danger"
                    bsSize="large"
                    onClick={this.handleStopTranslation}
                    style={{ 
                      fontSize: '18px', 
                      padding: '14px 32px',
                      background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                      border: 'none',
                      animation: 'pulse 1.5s infinite'
                    }}
                  >
                    <StopCircle size={18} style={{ marginRight: '8px' }} /> STOP / CANCELAR
                  </Button>
                )}
              </div>
              
              {/* Force Overwrite Checkbox */}
              <div style={{ 
                marginTop: '12px', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '8px 12px',
                backgroundColor: this.state.forceOverwrite ? '#ffebee' : '#f5f5f5',
                borderRadius: '6px',
                border: this.state.forceOverwrite ? '2px solid #d32f2f' : '1px solid #e0e0e0'
              }}>
                <input 
                  type="checkbox" 
                  id="forceOverwrite"
                  checked={this.state.forceOverwrite}
                  onChange={(e) => this.setState({ forceOverwrite: e.target.checked })}
                  disabled={this.state.translating}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label 
                  htmlFor="forceOverwrite" 
                  style={{ 
                    margin: 0, 
                    cursor: 'pointer',
                    fontWeight: this.state.forceOverwrite ? '600' : '400',
                    color: this.state.forceOverwrite ? '#c62828' : '#666'
                  }}
                >
                  <AlertTriangle size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Force Overwrite (Re-translate ALL, even existing translations)
                </label>
              </div>
              
              {/* Final Results */}
              {this.state.translationResult && !this.state.translating && (
                <div style={{ 
                  marginTop: '16px', 
                  padding: '16px', 
                  backgroundColor: this.state.translationResult.status === 'success' ? '#c8e6c9' : 
                    this.state.translationResult.status === 'partial' ? '#fff3e0' : '#ffcdd2',
                  borderRadius: '8px'
                }}>
                  <div style={{ 
                    fontWeight: '600', 
                    marginBottom: '8px',
                    color: this.state.translationResult.status === 'success' ? '#2e7d32' : 
                      this.state.translationResult.status === 'partial' ? '#e65100' : '#c62828'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {this.state.translationResult.status === 'success' ? <><CheckCircle size={18} /> Success!</> : 
                        this.state.translationResult.status === 'partial' ? <><AlertTriangle size={18} /> Partial Success</> : <><XCircle size={18} /> Failed</>}
                    </span>
                  </div>
                  <div>{this.state.translationResult.message}</div>
                  {this.state.translationResult.details && (
                    <div style={{ marginTop: '12px', fontSize: '13px' }}>
                      {this.state.translationResult.details.map((r, idx) => (
                        <div key={idx} style={{ 
                          padding: '4px 8px',
                          backgroundColor: 'rgba(255,255,255,0.5)',
                          borderRadius: '4px',
                          marginBottom: '4px',
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{r.status === 'success' ? <CheckCircle size={14} /> : <XCircle size={14} />} {r.section}</span>
                          <span>{r.translated || 0} / {r.skipped || 0} / {r.errors || 0}</span>
                        </div>
                      ))}
                      <div style={{ marginTop: '8px', fontSize: '11px', color: '#666' }}>
                        Format: translated / skipped / errors
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {uploadError && (
              <div className="alert alert-danger" style={{ marginBottom: 15 }}>{uploadError}</div>
            )}
            {successMessage && (
              <div className="alert alert-success" style={{ marginBottom: 15 }}>{successMessage}</div>
            )}
            {/* Quick links removed - functionality consolidated in sidebar menu */}

            {/* Edit Language Modal */}
            <Modal show={showLanguageModal} onHide={() => this.setState({ showLanguageModal: false })} bsSize="large">
              <Modal.Header closeButton>
                <Modal.Title>Edit Language: {editingLang && editingLang.title}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {/* Search Bar */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                  <FormControl
                    type="text"
                    placeholder="Search in JSON..."
                    value={this.state.searchTerm}
                    onChange={this.handleSearchChange}
                    inputRef={this.searchInputRef}
                    style={{ width: 250, marginRight: 10 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        this.handleSearchDown();
                      }
                    }}
                  />
                  <Button
                    onClick={this.handleSearchUp}
                    disabled={this.state.searchMatches.length === 0}
                    style={{ marginRight: 5 }}
                    title="Previous match"
                  >
                    ↑
                  </Button>
                  <Button
                    onClick={this.handleSearchDown}
                    disabled={this.state.searchMatches.length === 0}
                    title="Next match"
                  >
                    ↓
                  </Button>
                  {this.state.searchMatches.length > 0 && (
                    <span style={{ marginLeft: 10, fontSize: 14, color: '#555' }}>
                      {this.state.currentMatchIndex + 1} / {this.state.searchMatches.length}
                    </span>
                  )}
                  {this.state.searchTerm && this.state.searchMatches.length === 0 && (
                    <span style={{ marginLeft: 10, fontSize: 14, color: '#999' }}>
                      No matches found
                    </span>
                  )}
                </div>
                <FormGroup>
                  <ControlLabel>JSON Content</ControlLabel>
                  <FormControl
                    componentClass="textarea"
                    rows={20}
                    value={editingJson}
                    onChange={this.handleJsonChange}
                    inputRef={this.textareaRef}
                    style={{ fontFamily: 'monospace', fontSize: 13 }}
                  />
                </FormGroup>
              </Modal.Body>
              <Modal.Footer>
                <Button onClick={this.handleSave} className={cs.btnPrimary}>Save</Button>
                <Button onClick={() => this.setState({ showLanguageModal: false })}>Cancel</Button>
              </Modal.Footer>
            </Modal>

            {/* Email Templates moved to /siteadmin/smart-email-templates */}

          </div>
        </div>
      </AdminLayout>
    );
  }
}

const LanguageManagementWithStyles = withStyles(s, cs)(LanguageManagement);

export default async function action() {
  return {
    title: 'Manage Languages',
    component: <LanguageManagementWithStyles />
  };
}